import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSession } from '../auth/session.tsx';
import { useMySections } from '../api/navigation.ts';
import { downloadUpload, fetchUpload } from '../api/uploads.ts';
import { downloadClientDocument, fetchDocument } from '../api/documents.ts';
import { uploadViewer } from '../textPreview.ts';
import { recordDocumentState, type RecordDocumentFields } from '../legal/recordDocument.ts';
import { DocumentPreview, type PreviewDoc } from './DocumentPreview.tsx';
import { useDismissGuard } from './dismissGuard.tsx';

const viewButton = 'min-h-[44px] text-sm font-semibold text-neutral-700 underline underline-offset-2';

/**
 * A commission's or an oath's document, wherever its record is shown (P-20): `control` is "View document" or the words
 * recordDocumentState() gives, for the record's line; `preview` is the Documents section's own preview, the same
 * audited open as there, in its OWN overlay at its Documents size (A-#247): a native <dialog>, so it sits above the
 * card or panel, Escape and Close shut it, and the browser returns focus to "View document". The caller places it
 * after the line (a dialog cannot sit inside a line's paragraph). A Clio
 * copy's name is never read here (the application holds nothing on clio_copy), so its preview is titled by what it is;
 * it is tried as a PDF, and anything the server serves otherwise is offered as a download, as the preview does for any
 * file.
 */
export function useRecordDocument(clientId: string, record: RecordDocumentFields | null, what: string, none?: string): {
  control: ReactNode; preview: ReactNode;
  /** Whether the document has been opened here at least once (180: a row confirmed beside its page, A-#791 2). */
  opened: boolean;
} {
  const { getToken } = useSession();
  const holds = useMySections().data?.sections.includes('documents') ?? false;
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const state = recordDocumentState(record, holds, none);
  if (state.kind === 'words') return { control: state.words, preview: null, opened: false };
  const doc: PreviewDoc = state.source === 'upload'
    ? { id: state.id, name: state.name, date: null, viewer: uploadViewer(state.name), kind: 'Document', bytes: null }
    : { id: state.id, name: `The ${what} document`, date: null, viewer: 'pdf', kind: 'Document', bytes: null };
  const download = () => {
    if (busy) return;
    setBusy(true); setFailed(null);
    const go = state.source === 'upload' ? downloadUpload(getToken, clientId, state.id) : downloadClientDocument(getToken, clientId, state.id);
    void go.catch((e: Error) => setFailed(e.message)).finally(() => setBusy(false));
  };
  return {
    control: <button type="button" className={viewButton} aria-expanded={open}
                     onClick={() => { setOpen((o) => !o); setOpened(true); }}>View document</button>,
    opened,
    preview: open ? (
      <PreviewOverlay onClose={() => setOpen(false)}>
        <DocumentPreview doc={doc} position={null} onStep={() => {}} onClose={() => setOpen(false)}
                         fetchBlob={() => (state.source === 'upload' ? fetchUpload(getToken, clientId, state.id) : fetchDocument(getToken, clientId, state.id))}
                         onDownload={download} downloading={busy} failed={failed} />
      </PreviewOverlay>
    ) : null,
  };
}

/** The preview on top of the page, at the width Documents gives it; its own Close and Escape end it. */
function PreviewOverlay({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const closed = useRef(onClose);
  closed.current = onClose;
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handle = () => closed.current();
    dialog.addEventListener('close', handle);
    if (!dialog.open) dialog.showModal();
    return () => { dialog.removeEventListener('close', handle); if (dialog.open) dialog.close(); };
  }, []);
  const dismiss = () => { if (ref.current?.open) ref.current.close(); else closed.current(); };
  // A-#195: the backdrop closes only when pressed and released on it; nothing here is typed, so nothing is asked
  const guard = useDismissGuard(dismiss, { changes: false });
  return (
    <dialog ref={ref} aria-label="Document preview" {...guard.dialogProps}
            className="m-auto w-[min(64rem,calc(100vw-2rem))] rounded-xl bg-panel p-0 text-neutral-900 shadow-xl backdrop:bg-black/40">
      <div className="max-h-[92vh] overflow-y-auto">{children}</div>
    </dialog>
  );
}
